/**
 * Simple Risk Metrics
 * Five key metrics with visual status indicators
 * Progressive disclosure: simple view + expandable details
 * Serves IA (technical metrics) while keeping RM-friendly
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Info } from 'lucide-react';

interface MetricDefinition {
  name: string;
  value: number;
  label: string;
  status: 'good' | 'warning' | 'poor';
  format?: 'percentage' | 'decimal' | 'number';
}

interface SimpleRiskMetricsProps {
  annualizedReturn: number;
  volatility: number;
  sharpeRatio: number;
  activeShare: number;
  maxDrawdown: number;
  // Optional: for expandable detailed section
  additionalMetrics?: {
    sortinoRatio?: number;
    calmarRatio?: number;
    beta?: number;
    informationRatio?: number;
    trackingError?: number;
  };
}

const SimpleRiskMetrics: React.FC<SimpleRiskMetricsProps> = ({
  annualizedReturn,
  volatility,
  sharpeRatio,
  activeShare,
  maxDrawdown,
  additionalMetrics
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const getStatus = (value: number, thresholds: { good: number; warning: number }, reverse = false): 'good' | 'warning' | 'poor' => {
    if (reverse) {
      if (value <= thresholds.good) return 'good';
      if (value <= thresholds.warning) return 'warning';
      return 'poor';
    } else {
      if (value >= thresholds.good) return 'good';
      if (value >= thresholds.warning) return 'warning';
      return 'poor';
    }
  };

  const metrics: MetricDefinition[] = [
    {
      name: 'Returns',
      value: annualizedReturn,
      label: 'Annual',
      status: getStatus(annualizedReturn, { good: 8, warning: 4 }),
      format: 'percentage'
    },
    {
      name: 'Risk',
      value: volatility,
      label: 'Volatility',
      status: getStatus(volatility, { good: 8, warning: 12 }, true),
      format: 'percentage'
    },
    {
      name: 'Quality',
      value: sharpeRatio,
      label: 'Sharpe',
      status: getStatus(sharpeRatio, { good: 1.0, warning: 0.5 }),
      format: 'decimal'
    },
    {
      name: 'Active',
      value: activeShare,
      label: 'Share',
      status: getStatus(activeShare, { good: 60, warning: 40 }),
      format: 'percentage'
    },
    {
      name: 'Downside',
      value: Math.abs(maxDrawdown),
      label: 'Max Drop',
      status: getStatus(Math.abs(maxDrawdown), { good: 5, warning: 10 }, true),
      format: 'percentage'
    }
  ];

  const formatValue = (value: number, format: string = 'decimal') => {
    switch (format) {
      case 'percentage':
        return `${value.toFixed(1)}%`;
      case 'decimal':
        return value.toFixed(2);
      case 'number':
        return value.toFixed(0);
      default:
        return value.toFixed(2);
    }
  };

  const getStatusColor = (status: 'good' | 'warning' | 'poor') => {
    switch (status) {
      case 'good':
        return 'bg-green-50 border-green-200 text-green-700';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-700';
      case 'poor':
        return 'bg-red-50 border-red-200 text-red-700';
    }
  };

  const getStatusIcon = (status: 'good' | 'warning' | 'poor') => {
    switch (status) {
      case 'good':
        return '✅';
      case 'warning':
        return '⚠️';
      case 'poor':
        return '❌';
    }
  };

  const getStatusText = (status: 'good' | 'warning' | 'poor') => {
    switch (status) {
      case 'good':
        return 'Good';
      case 'warning':
        return 'Moderate';
      case 'poor':
        return 'Poor';
    }
  };

  // Calculate overall risk level (1-5 dots)
  const goodCount = metrics.filter(m => m.status === 'good').length;
  const riskLevel = Math.min(5, Math.max(1, Math.round((goodCount / metrics.length) * 5)));

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h4 className="text-lg font-semibold text-gray-900 mb-4">Risk & Quality Metrics</h4>

      {/* Main Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
        {metrics.map((metric, index) => (
          <div
            key={index}
            className={`p-4 rounded-lg border text-center ${getStatusColor(metric.status)}`}
          >
            <div className="text-2xl font-bold mb-1">
              {formatValue(metric.value, metric.format)}
            </div>
            <div className="text-xs font-medium uppercase mb-1">{metric.name}</div>
            <div className="text-xs opacity-75">{metric.label}</div>
            <div className="text-lg mt-1">{getStatusIcon(metric.status)}</div>
            <div className="text-xs font-medium mt-1">{getStatusText(metric.status)}</div>
          </div>
        ))}
      </div>

      {/* Risk Level Indicator */}
      <div className="flex items-center gap-3 py-3 px-4 bg-gray-50 rounded-lg border border-gray-200">
        <span className="text-sm font-medium text-gray-700">Risk Level:</span>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((level) => (
            <div
              key={level}
              className={`w-3 h-3 rounded-full ${
                level <= riskLevel ? 'bg-[#F7B500]' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
        <span className="text-sm text-gray-600">
          ({riskLevel}/5) - Within IPS target range
        </span>
        <div className="ml-auto text-green-600 font-medium text-sm flex items-center gap-1">
          <span>✅</span>
          Compliant
        </div>
      </div>

      {/* Expandable Detailed Metrics */}
      {additionalMetrics && (
        <div className="mt-4">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="w-full flex items-center justify-between py-2 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <div className="flex items-center gap-2">
              <Info className="w-4 h-4" />
              <span>Show detailed metrics</span>
            </div>
            {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {showDetails && (
            <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h5 className="text-sm font-semibold text-gray-900 mb-3">Advanced Risk Metrics</h5>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                {additionalMetrics.sortinoRatio !== undefined && (
                  <div>
                    <div className="text-gray-600">Sortino Ratio</div>
                    <div className="font-semibold text-gray-900">
                      {additionalMetrics.sortinoRatio.toFixed(2)}
                    </div>
                  </div>
                )}
                {additionalMetrics.calmarRatio !== undefined && (
                  <div>
                    <div className="text-gray-600">Calmar Ratio</div>
                    <div className="font-semibold text-gray-900">
                      {additionalMetrics.calmarRatio.toFixed(2)}
                    </div>
                  </div>
                )}
                {additionalMetrics.beta !== undefined && (
                  <div>
                    <div className="text-gray-600">Beta</div>
                    <div className="font-semibold text-gray-900">
                      {additionalMetrics.beta.toFixed(2)}
                    </div>
                  </div>
                )}
                {additionalMetrics.informationRatio !== undefined && (
                  <div>
                    <div className="text-gray-600">Information Ratio</div>
                    <div className="font-semibold text-gray-900">
                      {additionalMetrics.informationRatio.toFixed(2)}
                    </div>
                  </div>
                )}
                {additionalMetrics.trackingError !== undefined && (
                  <div>
                    <div className="text-gray-600">Tracking Error</div>
                    <div className="font-semibold text-gray-900">
                      {additionalMetrics.trackingError.toFixed(1)}%
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SimpleRiskMetrics;
